import { findSceneNode, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import { parseColor, type OklchColor } from "@/document/model/color";
import type { SceneNode } from "@/document/model/nodes";
import {
  createSolidPaint,
  normalizeGradientPaint,
  normalizeImagePaint,
  normalizeStrokeStack,
  type NodeStyle,
  type Paint,
} from "@/document/model/styles";
import type { RichTextModel } from "@/document/model/text";

export type SelectionColorScope = "fill" | "stroke" | "all";

export function isSelectionScopeContainer(node: SceneNode): boolean {
  return node.type === "frame" ||
    node.type === "group" ||
    node.type === "boolean" ||
    node.type === "component" ||
    node.type === "componentVariant" ||
    node.type === "instance";
}

export function nodeContributesOwnPaints(node: SceneNode): boolean {
  return node.type !== "group" &&
    node.type !== "component" &&
    node.type !== "componentVariant" &&
    node.type !== "instance";
}

export function showsSelectionColorsSection(selectedNodeIds: string[], document: CurvaDocument): boolean {
  if (selectedNodeIds.length === 0) {
    return false;
  }
  if (selectedNodeIds.length > 1) {
    return true;
  }

  const node = findSceneNode(document, selectedNodeIds[0]!);
  return node ? isSelectionScopeContainer(node) : false;
}

export function collectSelectionScopeNodes(document: CurvaDocument, selectedNodeIds: string[]): SceneNode[] {
  const nodes: SceneNode[] = [];
  const seen = new Set<string>();

  for (const nodeId of selectedNodeIds) {
    const node = findSceneNode(document, nodeId);
    if (!node) {
      continue;
    }
    collectNodeScope(node, nodes, seen);
  }

  return nodes;
}

function collectNodeScope(node: SceneNode, nodes: SceneNode[], seen: Set<string>): void {
  if (seen.has(node.id)) {
    return;
  }

  seen.add(node.id);
  nodes.push(node);

  if (!isSelectionScopeContainer(node)) {
    return;
  }

  const children = getSceneNodeChildren(node);
  if (!children) {
    return;
  }

  for (const child of children) {
    collectNodeScope(child, nodes, seen);
  }
}

export function oklchColorsEqual(left: OklchColor, right: OklchColor): boolean {
  const a = parseColor(left);
  const b = parseColor(right);
  return a.l === b.l && a.c === b.c && a.h === b.h && a.alpha === b.alpha;
}

export function compareOklchColors(left: OklchColor, right: OklchColor): number {
  const a = parseColor(left);
  const b = parseColor(right);
  if (a.l !== b.l) {
    return a.l - b.l;
  }
  if (a.c !== b.c) {
    return a.c - b.c;
  }
  if (a.h !== b.h) {
    return a.h - b.h;
  }
  return a.alpha - b.alpha;
}

export function sortOklchColors(colors: OklchColor[]): OklchColor[] {
  return [...colors].sort(compareOklchColors);
}

export function paintKey(paint: Paint): string {
  if (paint.type === "solid") {
    const color = parseColor(paint.color);
    return `solid:${color.l}:${color.c}:${color.h}:${color.alpha}`;
  }

  if (paint.type === "linearGradient" || paint.type === "radialGradient") {
    const normalized = normalizeGradientPaint(paint);
    const stops = normalized.stops
      .map(stop => `${stop.position}:${stop.color.l}:${stop.color.c}:${stop.color.h}:${stop.color.alpha}`)
      .join("|");
    if (normalized.type === "linearGradient") {
      return `linear:${normalized.angle}:${normalized.colorSpace}:${stops}`;
    }
    return `radial:${normalized.radius}:${normalized.colorSpace}:${stops}`;
  }

  const normalized = normalizeImagePaint(paint);
  return `image:${normalized.assetId ?? normalized.source ?? ""}:${normalized.opacity}:${normalized.fillMode}`;
}

export function selectionPaintRowKey(paint: Paint): string {
  if (paint.type === "solid") {
    const color = parseColor(paint.color);
    return `solid:${color.l}:${color.c}:${color.h}`;
  }

  if (paint.type === "linearGradient" || paint.type === "radialGradient") {
    const normalized = normalizeGradientPaint(paint);
    const stops = normalized.stops
      .map(stop => `${stop.position}:${stop.color.l}:${stop.color.c}:${stop.color.h}`)
      .join("|");
    if (normalized.type === "linearGradient") {
      return `linear:${normalized.angle}:${normalized.colorSpace}:${stops}`;
    }
    return `radial:${normalized.radius}:${normalized.colorSpace}:${stops}`;
  }

  const normalized = normalizeImagePaint(paint);
  return `image:${normalized.assetId ?? normalized.source ?? ""}:${normalized.fillMode}`;
}

export function paintsEqual(left: Paint, right: Paint): boolean {
  return paintKey(left) === paintKey(right);
}

export function paintsMatchForReplace(paint: Paint, fromPaint: Paint): boolean {
  return selectionPaintRowKey(paint) === selectionPaintRowKey(fromPaint);
}

function representativeColor(paint: Paint): OklchColor {
  if (paint.type === "solid") {
    return parseColor(paint.color);
  }

  if (paint.type === "linearGradient" || paint.type === "radialGradient") {
    const normalized = normalizeGradientPaint(paint);
    return parseColor(normalized.stops[0]?.color ?? { l: 0, c: 0, h: 0, alpha: 1 });
  }

  return parseColor({ l: 0.5, c: 0, h: 0, alpha: normalizeImagePaint(paint).opacity });
}

export function comparePaints(left: Paint, right: Paint): number {
  return compareOklchColors(representativeColor(left), representativeColor(right));
}

export function sortPaints(paints: Paint[]): Paint[] {
  return [...paints].sort(comparePaints);
}

function addUniquePaint(paints: Paint[], seen: Set<string>, paint: Paint | undefined): void {
  if (!paint) {
    return;
  }

  const key = paintKey(paint);
  if (seen.has(key)) {
    return;
  }

  seen.add(key);
  paints.push(paint);
}

function collectFillPaintsFromStyle(style: NodeStyle, paints: Paint[], seen: Set<string>): void {
  for (const fill of style.fills) {
    addUniquePaint(paints, seen, fill);
  }
}

function collectStrokePaintsFromStyle(style: NodeStyle, paints: Paint[], seen: Set<string>): void {
  for (const stroke of normalizeStrokeStack(style)) {
    addUniquePaint(paints, seen, stroke.paint);
  }
}

function collectFillPaintsFromText(text: RichTextModel, paints: Paint[], seen: Set<string>): void {
  addUniquePaint(paints, seen, text.style.fill);
  for (const override of Object.values(text.styleOverrideTable)) {
    addUniquePaint(paints, seen, override.fill);
  }
}

export function collectSelectionFillPaints(nodes: SceneNode[]): Paint[] {
  const paints: Paint[] = [];
  const seen = new Set<string>();

  for (const node of nodes) {
    if (nodeContributesOwnPaints(node)) {
      collectFillPaintsFromStyle(node.style, paints, seen);
    }
    if (node.type === "text") {
      collectFillPaintsFromText(node.text, paints, seen);
    }
  }

  return sortPaints(paints);
}

export function collectSelectionStrokePaints(nodes: SceneNode[]): Paint[] {
  const paints: Paint[] = [];
  const seen = new Set<string>();

  for (const node of nodes) {
    if (nodeContributesOwnPaints(node)) {
      collectStrokePaintsFromStyle(node.style, paints, seen);
    }
  }

  return sortPaints(paints);
}

export function collectSelectionAllPaints(nodes: SceneNode[]): Paint[] {
  return mergePaintsByIdentity(
    collectSelectionFillPaints(nodes),
    collectSelectionStrokePaints(nodes),
  );
}

export function mergePaintsByIdentity(primary: Paint[], secondary: Paint[]): Paint[] {
  const byIdentity = new Map<string, Paint>();
  for (const paint of primary) {
    byIdentity.set(selectionPaintRowKey(paint), paint);
  }
  for (const paint of secondary) {
    const identity = selectionPaintRowKey(paint);
    if (!byIdentity.has(identity)) {
      byIdentity.set(identity, paint);
    }
  }
  return sortPaints([...byIdentity.values()]);
}

function replacePaintInPaint(paint: Paint, fromPaint: Paint, toPaint: Paint): Paint {
  if (!paintsMatchForReplace(paint, fromPaint)) {
    return paint;
  }
  return {
    ...structuredClone(toPaint),
    id: paint.id,
  };
}

export function replacePaintInNodeStyle(
  style: NodeStyle,
  fromPaint: Paint,
  toPaint: Paint,
  scope: SelectionColorScope,
): NodeStyle {
  let next = style;

  if (scope === "fill" || scope === "all") {
    const fills = style.fills.map(fill => replacePaintInPaint(fill, fromPaint, toPaint));
    if (fills.some((fill, index) => fill !== style.fills[index])) {
      next = { ...next, fills };
    }
  }

  if (scope === "stroke" || scope === "all") {
    const strokes = normalizeStrokeStack(style).map(stroke => ({
      ...stroke,
      paint: replacePaintInPaint(stroke.paint, fromPaint, toPaint),
    }));
    const previousStrokes = normalizeStrokeStack(style);
    if (strokes.some((stroke, index) => stroke.paint !== previousStrokes[index]?.paint)) {
      next = {
        ...next,
        strokes,
        stroke: strokes.at(-1),
      };
    }
  }

  return next;
}

export function replacePaintInTextModel(
  text: RichTextModel,
  fromPaint: Paint,
  toPaint: Paint,
): RichTextModel | undefined {
  let changed = false;

  let nextStyle = text.style;
  if (text.style.fill && paintsMatchForReplace(text.style.fill, fromPaint)) {
    nextStyle = {
      ...text.style,
      fill: {
        ...structuredClone(toPaint),
        id: text.style.fill.id,
      },
    };
    changed = true;
  }

  const nextOverrideTable = { ...text.styleOverrideTable };
  for (const [id, override] of Object.entries(text.styleOverrideTable)) {
    if (override.fill && paintsMatchForReplace(override.fill, fromPaint)) {
      nextOverrideTable[Number(id)] = {
        ...override,
        fill: {
          ...structuredClone(toPaint),
          id: override.fill.id,
        },
      };
      changed = true;
    }
  }

  if (!changed) {
    return undefined;
  }

  return {
    ...text,
    style: nextStyle,
    styleOverrideTable: nextOverrideTable,
  };
}

/** @deprecated Use replacePaintInNodeStyle */
export function replaceSolidColorInNodeStyle(
  style: NodeStyle,
  fromColor: OklchColor,
  toColor: OklchColor,
  scope: SelectionColorScope,
): NodeStyle {
  return replacePaintInNodeStyle(style, createSolidPaint(fromColor), createSolidPaint(toColor), scope);
}

/** @deprecated Use replacePaintInTextModel */
export function replaceSolidColorInTextModel(
  text: RichTextModel,
  fromColor: OklchColor,
  toColor: OklchColor,
): RichTextModel | undefined {
  return replacePaintInTextModel(text, createSolidPaint(fromColor), createSolidPaint(toColor));
}

/** @deprecated Use collectSelectionFillPaints */
export function collectSelectionFillColors(nodes: SceneNode[]): OklchColor[] {
  return collectSelectionFillPaints(nodes)
    .filter((paint): paint is Extract<Paint, { type: "solid" }> => paint.type === "solid")
    .map(paint => parseColor(paint.color));
}

/** @deprecated Use collectSelectionStrokePaints */
export function collectSelectionStrokeColors(nodes: SceneNode[]): OklchColor[] {
  return collectSelectionStrokePaints(nodes)
    .filter((paint): paint is Extract<Paint, { type: "solid" }> => paint.type === "solid")
    .map(paint => parseColor(paint.color));
}

/** @deprecated Use collectSelectionAllPaints */
export function collectSelectionAllColors(nodes: SceneNode[]): OklchColor[] {
  return collectSelectionAllPaints(nodes)
    .filter((paint): paint is Extract<Paint, { type: "solid" }> => paint.type === "solid")
    .map(paint => parseColor(paint.color));
}
